import Markdown from 'markdown-to-jsx';
import type {ReactElement, ReactNode} from 'react';
import {useRef, useEffect, useState, memo} from 'react';
import {createPortal} from 'react-dom';
import styles from './markdown-renderer.module.scss';
import DOMPurify from 'dompurify';

function MarkdownRenderer({content, suffix}: {content: string; suffix?: ReactNode}): ReactElement {
  const contentRef = useRef<HTMLDivElement>(null);
  const [suffixContainer, setSuffixContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!contentRef.current || !suffix) {
      setSuffixContainer(null);
      return;
    }

    const rafId = requestAnimationFrame(() => {
      if (!contentRef.current) return;

      const paragraphs = contentRef.current.querySelectorAll('p');
      if (paragraphs.length === 0) {
        setSuffixContainer(null);
        return;
      }

      const lastParagraph = paragraphs[paragraphs.length - 1];
      let container = lastParagraph.querySelector<HTMLSpanElement>('.markdown-suffix');

      if (!container) {
        container = document.createElement('span');
        container.className = 'markdown-suffix';
        container.style.display = 'inline';
        container.style.marginLeft = '2px';
        lastParagraph.appendChild(container);
      }

      setSuffixContainer(container);
    });

    return (): void => {
      cancelAnimationFrame(rafId);
    };
  }, [content, suffix]);

  const sanitizedContent = DOMPurify.sanitize(content, {
    USE_PROFILES: {html: true},
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form'],
  });

  return (
    <div ref={contentRef} className={styles.content}>
      <Markdown
        options={{
          forceBlock: true,
        }}
      >
        {sanitizedContent}
      </Markdown>

      {suffixContainer && suffix ? createPortal(suffix, suffixContainer) : null}
    </div>
  );
}

export default memo(MarkdownRenderer);
